/// <reference types="vite/client" />

// React render-error infrastructure (error boundary handler), allowed to capture directly.
// oxlint-disable-next-line no-restricted-imports
import * as Sentry from '@sentry/react';
import { type AnyRouter, isRedirect } from '@tanstack/react-router';
import isNetworkError from 'is-network-error';
import {
  appendSentryContext,
  isFetchError,
  isModuleNotFoundError,
  isPublicApiError,
  isSignalAbortError,
  isSignalTimeoutError,
  isTimeoutError,
} from '@/api/errors';
import { reportMessage } from '@/helpers/errors/report';
import { attempt, chain } from '@/helpers/functional';
import { WEB_STORAGE_PREFIX } from '@/utils/constants';

let isVitePreloadError = false;

export const resetVitePreloadError = () => {
  isVitePreloadError = false;
};

window.addEventListener('vite:preloadError', (_event: VitePreloadErrorEvent) => {
  isVitePreloadError = true;
});

export function isViteModuleNotFoundError(error: unknown): error is Error {
  return isVitePreloadError || isModuleNotFoundError(error);
}

export const createReactErrorHandler = (
  router: AnyRouter,
  ...handlers: ((error: unknown, errorInfo: { componentStack?: string | undefined }) => void)[]
) =>
  chain<[error: unknown, errorInfo: { componentStack?: string | undefined }]>(
    /**
     * Follows the same logic as the `lazyRouteComponent` utility in TanStack Router
     * to detect module not found errors and trigger a reload to recover from them.
     * @see https://github.com/TanStack/router/blob/77ad7925d4a6ea88ecd523ca7445ca192ce7a91a/packages/react-router/src/lazyRouteComponent.tsx#L69
     */
    function moduleNotFoundErrorHandler(error: unknown) {
      if (isViteModuleNotFoundError(error)) {
        // We want to reload one time on module not found error and not enter
        // a reload loop if there is some other issue besides an old deploy.
        // That's why we store our reload attempt in sessionStorage.
        // Use error.message as key because it contains the module path that failed.
        const storageKey = `${WEB_STORAGE_PREFIX}${error.message}`;
        const hasError = attempt<boolean, boolean>(
          () => Boolean(sessionStorage.getItem(storageKey)),
          // Else return true to mimic that we've already reloaded; as `sessionStorage` throws, and we can't continue.
          () => true,
        );
        // If we haven't reloaded yet, let's create a flag to later know we were reloading.
        if (!hasError) {
          // Without a stored flag the next load could reload again, so skip the reload.
          // `attempt` also keeps a storage error from blocking the capture handler that runs next.
          const isFlagStored = attempt(() => {
            sessionStorage.setItem(storageKey, '1');
            return true;
          });
          if (!isFlagStored) {
            return;
          }
          // A stale deploy (old pods replaced, old chunks gone) is fixed by the reload, so there is nothing to act on.
          // Flag it for `beforeSend` to drop. This handler runs before the capture handler, so the flag is set before
          // Sentry processes the event. Check the message: after `vite:preloadError`, any error reaches this branch.
          if (isModuleNotFoundError(error)) {
            appendSentryContext(error, { ignore: true });
            // Keep a count of stale-deploy reloads, below alert level.
            reportMessage('Reloading after a stale deploy', {
              domain: 'infra',
              level: 'info',
              extra: { reloadUrl: router.state.location.href, chunkError: error.message },
            });
          }
          // For chunk load errors, we're not in a transition, but we can still capture
          // the current location and where we would navigate after reload
          Sentry.addBreadcrumb({
            category: 'navigation',
            message: 'Reloading due to module not found error',
            data: {
              reloadUrl: router.state.location.href,
              chunkError: error.message,
            },
          });
          // Flush past 2 seconds to prevent the reload from swallowing events still in the queue,
          // which would be crucial for debugging.
          void Sentry.flush(2000).then(() => {
            window.location.reload();
          });
        }
      }
    },
    function captureErrorHandler(error: unknown, errorInfo: { componentStack?: string | undefined }) {
      if (
        !(
          isPublicApiError(error) ||
          isTimeoutError(error) ||
          isFetchError(error) ||
          isNetworkError(error) ||
          isSignalTimeoutError(error) ||
          isSignalAbortError(error) ||
          isRedirect(error)
        )
      ) {
        Sentry.captureReactException(error, errorInfo);
      }
    },
    ...handlers,
  );
